import type {
	CoreApiError,
	CoreApiPaginatedMetaResponse,
	DetailQueryOptions,
	HotelCollection,
	HotelItem,
	PaginatedParams,
	PaginatedQueryOptions,
} from '@lqa-frontend/api-services';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
	useDetailQuery,
	usePaginatedQuery,
} from '@lqa-frontend/api-services';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { UndefinedInitialDataInfiniteOptions } from '@tanstack/react-query';

export function useHotelsQuery(
	params: HotelsQueryParams,
	options?: PaginatedQueryOptions<HotelCollection>
) {
	return usePaginatedQuery(endpoints.hotels.index, params, options);
}

export function useHotelQuery(
	id?: number,
	options?: Omit<DetailQueryOptions<HotelItem>, 'queryKey' | 'queryFn'>
) {
	return useDetailQuery<HotelItem>(endpoints.hotels.item, id, options);
}

export interface HotelsQueryParams extends PaginatedParams {
	name?: string;
	brandIds?: number[];
	hotelIds?: number[];
	withCustomPricing?: boolean;
	contractId?: string;
	isActive?: boolean;
}

export function useHotelsInfiniteQuery(
	params: HotelsQueryParams,
	options?: HotelsInfiniteQueryOptions
) {
	return useInfiniteQuery<
		CoreApiPaginatedMetaResponse<HotelCollection>,
		AxiosError<CoreApiError>
	>({
		queryKey: coreApiQueryKey(endpoints.myLQA.pricing.hotels, 'get', params),
		queryFn: async ({ pageParam = 1 }) =>
			coreApiClient
				.get<CoreApiPaginatedMetaResponse<HotelCollection>>(
					endpoints.myLQA.pricing.hotels,
					{
						params: {
							...params,
							page: pageParam,
						},
					}
				)
				.then((response) => response.data),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.meta.currentPage < lastPage.meta.totalPages
				? lastPage.meta.currentPage + 1
				: null,
		...options,
	});
}

export type HotelsInfiniteQueryOptions = Omit<
	UndefinedInitialDataInfiniteOptions<
		CoreApiPaginatedMetaResponse<HotelCollection>,
		AxiosError<CoreApiError>
	>,
	'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;
